/**
 * Merges downloaded questionnaire template JSON bodies (from
 * `GET …/questionnaire` → S3) into a single `FlowTemplate` the engine walks.
 *
 * Production path: pass templates in API response order — no client-side
 * package filtering. Mock simulation of catalogue order lives in
 * `getMockComposedFlowTemplate.ts` + `estatePackageSectionPlans.ts`.
 */
import type {FlowTemplate, ModalDef, Section} from '../../containers/estatePlanning/resolvers/types';

/** Shape of one downloaded template JSON file (section file or modals file). */
export type EstateTemplateBody = {
  id?: string;
  sections?: Section[];
  modals?: Record<string, ModalDef>;
  addressStateGate?: FlowTemplate['addressStateGate'];
};

/** One entry from `QuestionnaireResponse.templates[]` after its JSON body is downloaded. */
export type DownloadedEstateTemplate = {
  templateId?: string;
  body: EstateTemplateBody;
};

export type ComposeEstateFlowTemplateOptions = {
  /** Flow-level id (e.g. from session metadata or first template). */
  flowTemplateId?: string;
};

const DEFAULT_FLOW_TEMPLATE_ID = 'estate_planning';

/**
 * Composes downloaded template bodies in API order: concatenates `sections`,
 * merges `modals`, and copies `addressStateGate` from the first body that
 * declares it (typically about-you).
 */
export function composeEstateFlowTemplateFromDownloads(
  templates: DownloadedEstateTemplate[],
  options: ComposeEstateFlowTemplateOptions = {}
): FlowTemplate {
  const sections: Section[] = [];
  const modals: Record<string, ModalDef> = {};
  let addressStateGate: FlowTemplate['addressStateGate'];

  for (const {body} of templates) {
    if (body.sections?.length) {
      sections.push(...body.sections);
    }

    if (body.modals) {
      Object.assign(modals, body.modals);
    }

    if (!addressStateGate && body.addressStateGate) {
      addressStateGate = body.addressStateGate;
    }
  }

  return {
    id: options.flowTemplateId ?? DEFAULT_FLOW_TEMPLATE_ID,
    sections,
    modals,
    ...(addressStateGate ? {addressStateGate} : {}),
  };
}
